import { useCheckoutSingleCourseMutation } from '@Features/Classroom/ClassroomSlice/ClassroomSlice'
import { useCheckoutStandaloneCourseMutation } from '@Features/StandaloneCourse/StandaloneCourseApi/StandaloneCourseApi'
import { useCheckoutBundleMutation } from '@Features/Bundle/BundleApi/BundleApi'
import { useLazyVerifyPromoCodeQuery } from '@Features/Base/BaseApi/BaseApi'
import { useVerifyStandalonePromocodeMutation } from '@Features/StandaloneCourse/StandaloneCourseApi/StandaloneCourseApi'
import { useVerifyBundlePromocodeMutation } from '@Features/Bundle/BundleApi/BundleApi'
import type { PaymentModalVariant } from './PaymentModal'

type CheckoutExtras = { payment_method: 'wallet' | 'scratchCard' | 'free'; code?: string; promocode?: string }

/**
 * The single place the payment variant → (checkout endpoint, id field) mapping
 * lives. The three mutation hooks are still called unconditionally (rules of
 * hooks), but every consumer (WalletPayment / ScratchPayment) gets one
 * `checkout(extras)` + one `state` resolved by variant — so adding a new
 * purchasable type touches this file, not each payment component.
 */
export const usePaymentCheckout = (variant: PaymentModalVariant, id: number | string) => {
	const [checkoutCourse, courseState] = useCheckoutSingleCourseMutation()
	const [checkoutStandalone, standaloneState] = useCheckoutStandaloneCourseMutation()
	const [checkoutBundle, bundleState] = useCheckoutBundleMutation()

	const state = variant === 'bundle' ? bundleState : variant === 'standaloneCourse' ? standaloneState : courseState

	const checkout = (extras: CheckoutExtras) => {
		if (variant === 'bundle') return checkoutBundle({ bundle_id: Number(id), ...extras })
		if (variant === 'standaloneCourse') return checkoutStandalone({ standalone_course_id: Number(id), ...extras })
		// Number(id), like the bundle and standalone branches above — `id` is
		// string | number here and course_id is a number.
		return checkoutCourse({ course_id: Number(id), ...extras })
	}

	return { checkout, state }
}

/**
 * The promocode-verify counterpart of usePaymentCheckout — resolves the verify
 * endpoint + id field by variant. Returns a `verify(code)` that resolves to the
 * standard ApiResponse<{ value }> the button reads (value = discount amount).
 */
export const usePaymentPromocode = (variant: PaymentModalVariant, id: number | string) => {
	const [verifyCourse] = useLazyVerifyPromoCodeQuery()
	const [verifyStandalone] = useVerifyStandalonePromocodeMutation()
	const [verifyBundle] = useVerifyBundlePromocodeMutation()

	return (code: string) => {
		if (variant === 'bundle') return verifyBundle({ bundle_id: Number(id), code }).unwrap()
		if (variant === 'standaloneCourse') return verifyStandalone({ standalone_course_id: Number(id), code }).unwrap()
		return verifyCourse({ course_id: Number(id), code }).unwrap()
	}
}
